<template>
    <div
        class="liveStreamAndBibleCloudLayout"
        :class="{'liveStreamAndBibleCloudLayout-no-sidebar': !hasSidebar}"
        :style="colorStyles"
    >
        <div
            v-show="isNavExpanded || showOverlay"
            @click="$emit('overlayClicked')"
            class="navbar-expanded-overlay"
            :class="!isNavExpanded ? overlayClass : ''"
        ></div>

        <!-- Top navbar -->
        <nav
            class="navbar navbar-dark navbar-expand-lg"
            :class="{
                'navbar-expanded': isNavExpanded

            }"
        >
            <div class="navbar-logo-wrapper">
                <div>
                    <a
                        v-if="settings.logo_url"
                        href="/"
                    >
                        <img
                            alt="logo"
                            class="navbar-logo-wrapper-img"
                            height="60"
                            :src="settings.logo_url"
                        />
                    </a>
                </div>
            </div>

            <div class="d-flex">
                <!-- Allow additional content to appear in the navbar, to the right of the hamburger menu -->
                <slot name="navbar-extras"></slot>

                <!-- Only displays on smaller browsers / mobile devices that have navigation items -->
                <button
                    v-if="hasNavigation"
                    aria-controls="navbarNavAltMarkup"
                    aria-expanded="false"
                    aria-label="Toggle navigation"
                    class="navbar-toggler"
                    data-toggle="collapse"
                    data-target="#navbarNavAltMarkup"
                    ref="navbarToggler"
                    type="button"
                >
                    <span class="navbar-toggler-icon"></span>
                </button>
            </div>

            <!-- Actual navigation items, displayed on larger browsers and collapsable on smaller ones -->
            <div
                class="collapse navbar-collapse"
                id="navbarNavAltMarkup"
            >
                <div class="navbar-nav">
                    <!-- Links from e360's link list -->
                    <a
                        v-for="nav in settings.navigation"
                        class="nav-link"
                        :href="nav.url"
                        :target="nav.ifnewwindow ? '_blank' : ''"
                        :title="nav.name"
                    >
                        {{ nav.name }}
                    </a>

                    <!-- Hardcoded social media icon links -->
                    <div class="navbar-social">
                        <a
                            v-if="church.social.facebook.link"
                            class="nav-link nav-link-social"
                            :href="church.social.facebook.link"
                            target="_blank"
                            title="Visit us on Facebook"
                        >
                            <simple-svg :filepath="getSvgFilePath('facebook-circle')"></simple-svg>
                        </a>

                        <a
                            v-if="church.social.twitter.link"
                            class="nav-link nav-link-social"
                            :href="church.social.twitter.link"
                            target="_blank"
                            title="Visit us on Twitter"

                        >
                            <simple-svg :filepath="getSvgFilePath('twitter')"></simple-svg>
                        </a>

                        <a
                            v-if="church.social.instagram.link"
                            class="nav-link nav-link-social"
                            :href="church.social.instagram.link"
                            target="_blank"
                            title="Visit us on Instagram"
                        >
                            <simple-svg :filepath="getSvgFilePath('instagram')"></simple-svg>
                        </a>

                        <a
                            v-if="church.social.youtube.link"
                            class="nav-link nav-link-social"
                            :href="church.social.youtube.link"
                            target="_blank"
                            title="Visit us on YouTube"
                        >
                            <simple-svg :filepath="getSvgFilePath('youtube')"></simple-svg>
                        </a>

                        <a
                            v-if="church.social.vimeo.link"
                            class="nav-link nav-link-social"
                            :href="church.social.vimeo.link"
                            target="_blank"
                            title="Visit us on Vimeo"
                        >
                            <simple-svg :filepath="getSvgFilePath('vimeo')"></simple-svg>
                        </a>
                    </div>
                </div>
            </div>
        </nav>

        <div class="row no-gutters position-relative">
            <div
                class="liveStreamAndBibleCloudLayout-container col-12"
                :class="[containerClass, {
                    'col-lg-9': hasSidebar
                }]"
            >
                <!-- Main content for the page -->
                <slot name="content"></slot>
            </div>

            <!-- Content displayed on the side for larger browsers and under the stream for smaller browsers -->
            <div
                v-if="hasSidebar"
                class="liveStreamAndBibleCloudLayout-sidebar col-12 col-lg-3"
            >

                <!-- Allow some content (like a countdown) to display above the sidebar -->
                <slot name="sidebar-header"></slot>

                <div
                    class="liveStreamAndBibleCloudLayout-sidebar-toggles"
                    :class="sidebarClass"
                >
                    <div v-for="section in sortedSections" :key="section.key" class="flex-container">
                        <template v-if="section.key === 'chat'">
                            <div
                                @click="toggleTab('chat')"
                                @keyup.enter="toggleTab('chat')"
                                class="liveStreamAndBibleCloudLayout-sidebar-toggle-header"
                                :class="{'collapsed': !showChat}"
                                tabindex="0"
                            >
                                <div class="liveStreamAndBibleCloudLayout-sidebar-toggle-header-text">
                                    <simple-svg :filepath="getSvgFilePath('chat')"></simple-svg>
                                    <span>
                                        CHAT
                                    </span>
                                </div>

                                <simple-svg
                                    class="chevron-arrow chevron-arrow-down"
                                    :filepath="getSvgFilePath('chevron-down')"
                                ></simple-svg>
                                <simple-svg
                                    class="chevron-arrow chevron-arrow-up"
                                    :filepath="getSvgFilePath('chevron-up')"
                                ></simple-svg>
                            </div>

                            <div
                                class="liveStreamAndBibleCloudLayout-sidebar-toggle-body"
                                :class="{'collapse': !showChat}"
                            >
                                <span v-html="settings.chatembed"></span>
                            </div>
                        </template>

                        <template v-if="section.key === 'prayer' && createPrayerUrl">
                            <div
                                @click="toggleTab('prayer')"
                                @keyup.enter="toggleTab('prayer')"
                                class="liveStreamAndBibleCloudLayout-sidebar-toggle-header"
                                :class="{'collapsed': !showPrayer}"
                                tabindex="0"
                            >
                                <div class="liveStreamAndBibleCloudLayout-sidebar-toggle-header-text">
                                    <simple-svg :filepath="getSvgFilePath('prayer')"></simple-svg>
                                    <span>
                                        PRAYER
                                    </span>
                                </div>

                                <simple-svg
                                    class="chevron-arrow chevron-arrow-down"
                                    :filepath="getSvgFilePath('chevron-down')"
                                ></simple-svg>
                                <simple-svg
                                    class="chevron-arrow chevron-arrow-up"
                                    :filepath="getSvgFilePath('chevron-up')"
                                ></simple-svg>
                            </div>

                            <div
                                class="liveStreamAndBibleCloudLayout-sidebar-toggle-body"
                                :class="{'collapse': !showPrayer}"
                            >
                                <sc-prayers
                                    :createPrayerUrl="createPrayerUrl"
                                    :prayer="settings.prayer"
                                ></sc-prayers>
                            </div>
                        </template>

                        <template v-if="section.key === 'give'">
                            <!-- TRIAL VERSION: Show disabled trial give form -->
                            <template v-if="isAmplifyStreamingTrial">
                                <div
                                    @click="toggleTab('give')"
                                    @keyup.enter="toggleTab('give')"
                                    class="liveStreamAndBibleCloudLayout-sidebar-toggle-header"
                                    :class="{'collapsed': !showGive}"
                                    tabindex="0"
                                >
                                    <div class="liveStreamAndBibleCloudLayout-sidebar-toggle-header-text">
                                        <simple-svg :filepath="getSvgFilePath('hand-holding-heart')"></simple-svg>
                                        <span>Give</span>
                                    </div>

                                    <simple-svg
                                        class="chevron-arrow chevron-arrow-down"
                                        :filepath="getSvgFilePath('chevron-down')"
                                    ></simple-svg>
                                    <simple-svg
                                        class="chevron-arrow chevron-arrow-up"
                                        :filepath="getSvgFilePath('chevron-up')"
                                    ></simple-svg>
                                </div>

                                <div
                                    class="liveStreamAndBibleCloudLayout-sidebar-toggle-body"
                                    :class="{'collapse': !showGive, 'no-padding': isAmplifyStreamingTrial}"
                                >
                                    <sc-give-form-trial
                                        :settings="settings"
                                        @upgrade-clicked="handleTrialUpgradeClick"
                                    ></sc-give-form-trial>
                                </div>
                            </template>

                            <!-- NORMAL VERSION: Show regular give form -->
                            <template v-else>
                                <div
                                    @click="toggleTab('give')"
                                    @keyup.enter="toggleTab('give')"
                                    class="liveStreamAndBibleCloudLayout-sidebar-toggle-header"
                                    :class="{'collapsed': !showGive}"
                                    tabindex="0"
                                >
                                    <div class="liveStreamAndBibleCloudLayout-sidebar-toggle-header-text">
                                        <simple-svg :filepath="getSvgFilePath('hand-holding-heart')"></simple-svg>
                                        <!-- Large and Medium screens -->
                                        <span class="d-none d-md-inline">
                                           {{ giveButtonText }}
                                        </span>
                                        <!-- Small screen only -->
                                        <span class="d-none d-sm-inline d-md-none">
                                           {{
                                                (giveButtonText.length <= 8) ? giveButtonText : giveButtonText.substring(0, 8) + '...'
                                            }}
                                        </span>
                                        <!-- Extra small screen only -->
                                        <span class="d-inline d-sm-none">
                                           {{
                                                (giveButtonText.length <= 5) ? giveButtonText : giveButtonText.substring(0, 5) + '...'
                                            }}
                                        </span>
                                    </div>

                                    <simple-svg
                                        class="chevron-arrow chevron-arrow-down"
                                        :filepath="getSvgFilePath('chevron-down')"
                                    ></simple-svg>
                                    <simple-svg
                                        class="chevron-arrow chevron-arrow-up"
                                        :filepath="getSvgFilePath('chevron-up')"
                                    ></simple-svg>
                                </div>

                                <div v-if="showPopupModel" class="modal-overlay" :class="{'collapse': !showGive}">
                                    <div id="custom-give-form-modal" ref="customGiveFormModal" :v-model="showPopupModel"
                                         role="dialog" tabindex="-1" class="modal fade show d-block" aria-modal="true">
                                        <div class="modal-dialog modal-md">
                                            <div role="document" id="custom-give-form-modal___BV_modal_content_"
                                                 aria-labelledby="custom-give-form-modal___BV_modal_header_"
                                                 aria-describedby="custom-give-form-modal___BV_modal_body_"
                                                 class="modal-content">
                                                <div>
                                                    <!-- Custom Loader - how spinner while the script is being loaded  -->
                                                    <div v-if="isLoadingGracePeriod" class="custom-loader-container">
                                                        <div class="custom-loader"></div>
                                                        <p>Loading...</p>
                                                    </div>

                                                    <div v-if="!giveFormScriptLoaded && !isLoadingGracePeriod"
                                                         class="give-form-not-found">
                                                        <p>Give Form is not available at the moment.</p>
                                                    </div>

                                                    <div id="sidebar-popup-give-form-container">
                                                        <span v-if="!isLoadingGracePeriod" class="close"
                                                              @click="hideGivePopupModal">×</span>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div tabindex="0"></div>
                                    <div id="custom-give-form-modal___BV_modal_backdrop_"
                                         class="modal-backdrop fade show"></div>
                                </div>
                                <div
                                    v-if="settings && settings.give && settings.give.give_form_experience === 'embed'"
                                    class="liveStreamAndBibleCloudLayout-sidebar-toggle-body give-form"
                                    :class="{'collapse': !showGive}"
                                >

                                    <!-- Custom Loader - how spinner while the script is being loaded  -->
                                    <div v-if="isLoadingGracePeriod" class="custom-loader-container">
                                        <div class="custom-loader"></div>
                                        <p>Loading...</p>
                                    </div>

                                    <!-- Show message only if the script has failed -->
                                    <div v-if="!giveFormScriptLoaded && !isLoadingGracePeriod" class="give-form-not-found">
                                        <p>Give Form is not available at the moment.</p>
                                    </div>

                                    <!-- The form container should always be present -->
                                    <div id="sidebar-embed-give-form-container"></div>

                                </div>
                            </template>
                        </template>

                        <template v-if="section.key === 'details' && detailsEnabled">
                            <div
                                @click="toggleTab('details')"
                                @keyup.enter="toggleTab('details')"
                                class="liveStreamAndBibleCloudLayout-sidebar-toggle-header"
                                :class="{'collapsed': !showDetails}"
                                tabindex="0"
                            >
                                <div class="liveStreamAndBibleCloudLayout-sidebar-toggle-header-text">
                                    <simple-svg :filepath="getSvgFilePath('file-alt')"></simple-svg>
                                    <span>
                                        DETAILS
                                    </span>
                                </div>

                                <simple-svg
                                    class="chevron-arrow chevron-arrow-down"
                                    :filepath="getSvgFilePath('chevron-down')"
                                ></simple-svg>
                                <simple-svg
                                    class="chevron-arrow chevron-arrow-up"
                                    :filepath="getSvgFilePath('chevron-up')"
                                ></simple-svg>
                            </div>

                            <div
                                class="liveStreamAndBibleCloudLayout-sidebar-toggle-body"
                                :class="{'collapse': !showDetails}"
                            >
                                <!-- Let the parent component determine the custom content, elements, and styling for the details tab -->
                                <slot name="details"></slot>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <!-- Display a reseller specific tagline, copyright, and logo -->
            <div
                v-if="site.tagline"
                class="liveStreamAndBibleCloudLayout-vendor-tagline"
            >
                <div>&copy;{{ new Date().getFullYear() }} {{ site.title }}</div>
                <simple-svg
                    class="liveStreamAndBibleCloudLayout-vendor-tagline-icon d-none d-sm-block"
                    :filepath="getSvgFilePath(site.tagline.logo_svg, '/')"
                    height="32px"
                ></simple-svg>
                <simple-svg
                    class="liveStreamAndBibleCloudLayout-vendor-tagline-icon d-sm-none"
                    :filepath="getSvgFilePath(site.tagline.logo_icon_svg)"
                    height="24px"
                ></simple-svg>
                <div class="d-none d-sm-block">Powered by: {{ site.title }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import {getSvgFilePathMixin} from '../../mixins/getSvgFilePathMixin';
import scPrayers from '../../components/streaming/scPrayers';
import scGiveFormTrial from '../../components/streaming/scGiveFormTrial';
import {SimpleSVG} from 'vue-simple-svg';

export default {
    components: {
        scPrayers,
        scGiveFormTrial,
        'simple-svg': SimpleSVG
    },
    mixins: [getSvgFilePathMixin],
    props: {
        church: Object,
        containerClass: String,
        createPrayerUrl: String,
        settings: Object,
        showOverlay: Boolean,
        detailsEnabled: Boolean,
        sidebarClass: String,
        site: Object,
        overlayClass: String,
        currentStream: Object
    },
    data() {
        return {
            isNavExpanded: false,
            lgBreakpoint: 992,
            showChat: false,
            showDetails: false,
            showPrayer: false,
            showGive: false,
            showPopupModel: false,
            giveFormScriptLoaded: false, // Track if script has loaded
            isLoadingGracePeriod: false // Track if the loading grace period is active
        }
    },
    watch: {
        settings: {
            handler(newVal) {
                // this.openDefaultTab(newVal);
            },
            deep: true,
            immediate: false
        }
    },
    mounted() {
        this.openDefaultTab(this.settings);

        const $navbarCollapse = $('.navbar-collapse');

        $navbarCollapse.on('show.bs.collapse', () => {
            this.isNavExpanded = true;
            this.$emit('navToggled', true);
        });

        $navbarCollapse.on('hidden.bs.collapse', () => {
            this.isNavExpanded = false;
            this.$emit('navToggled', false);
        });

        // Check if the give form is enabled and if form experience === 'embed' so, inject the script
        if (this.settings && this.settings.give && this.settings.give.give_form_experience === 'embed') {
            this.injectGiveForm("sidebar-embed-give-form-container", this.giveFormEmbedUrl, this.settings.give.give_form_experience);
        }
    },
    computed: {
        giveButtonText() {
            return (this.currentStream && this.currentStream.give_button_text && this.currentStream.give_button_text.trim())
                ? this.currentStream.give_button_text
                : (this.settings && this.settings.give ? this.settings.give.give_button_text : '');
        },
        /**
         * Expose user set css variables for use throughout the embed
         */
        colorStyles() {
            return {
                '--user-alternate-color': this.settings.alternate_color,
                '--user-header-color': this.settings.header_color,
                '--user-link-color': this.settings.link_color
            };
        },

        /**
         * Check if Amplify Streaming Trial is enabled
         */
        isAmplifyStreamingTrial() {
            return this.settings.is_amplify_streaming_trial === true || this.settings.is_amplify_streaming_trial === 1;
        },

        /**
         * Are there any items going to be displayed in the navigation
         */
        hasNavigation() {
            return !!this.settings.navigation.length ||
                !!this.church.social.facebook.link ||
                !!this.church.social.twitter.link ||
                !!this.church.social.instagram.link ||
                !!this.church.social.youtube.link ||
                !!this.church.social.vimeo.link;

        },

        /**
         * Determine if the sidebar has content to display
         */
        hasSidebar() {
            return !!this.$slots['sidebar-header'] ||
                !!this.settings.chatembed ||
                !!(this.settings.prayer.enabled && this.createPrayerUrl) ||
                !!this.$slots.details;

        },

        sortedSections() {
            const sequence = this.settings.feature_order || {};

            const sections = [
                {key: 'chat', enabled: this.settings.chatembed && this.settings.chatenabled},
                {key: 'prayer', enabled: this.settings.prayer && this.settings.prayer.enabled},
                {key: 'details', enabled: this.detailsEnabled},
                {key: 'give', enabled: this.settings.give && this.settings.give.enabled || this.isAmplifyStreamingTrial}
            ];

            return sections
                .filter(section => section.enabled)  // Only include enabled sections
                .sort((a, b) => {
                    // If sequence is empty or a key is, use fallback sorting by key
                    return (sequence[a.key] || 0) - (sequence[b.key] || 0);
                });
        },

        /**
         * Construct the give form embed script URL dynamically
         */
        giveFormEmbedUrl() {
            const giveFormDetails = this.settings.give.give_form_details;
            if (!giveFormDetails) return null;

            // Ensure the endpoint is defined and valid
            const endpoint = giveFormDetails.fms_endpoint;

            const externalCSS = giveFormDetails.externalcss;

            // Check if currentStream has a valid giving_form_id, otherwise fallback to giveFormDetails.id
            const id = this.currentStream && this.currentStream.giving_form_id
                ? (this.currentStream.giving_form_id.trim() !== '' ? this.currentStream.giving_form_id : giveFormDetails.id)
                : giveFormDetails.id;

            if (!endpoint || !id || !giveFormDetails.is_published) {
                return null;
            }


            let externalJsonData = (this.currentStream) ? `stream_id:${this.currentStream.id},form_experience:${this.settings.give.give_form_experience}` : '';

            // Adding source ID here as it will be use as fixed sidebar
            // SourceID 17 is for Streaming Page (Sidebar)
            return `${endpoint}/embed.aspx?formId=${id}&set-page-title=true&button-class=sites-button&use-default-theme=false&button-class=sites-button&style-url=${externalCSS}&source=17&external-data=${externalJsonData}`;
        }
    },
    methods: {
        /**
         * Expand / collapse a specific tab
         * On smaller browsers only a single tab can be shown at once
         * On larger browsers multiple tabs can be shown at once
         *
         * @param {String} tab - name of the tab to be shown
         */
        setOpenTab(tab) {
            const isSmallBrowser = window.innerWidth < this.lgBreakpoint;

            if (tab === 'chat' && !this.showChat) {
                this.showChat = true;
                if (isSmallBrowser) {
                    this.showPrayer = false;
                    this.showDetails = false;
                    this.showGive = false;
                }
            } else if (tab === 'prayer' && !this.showPrayer) {
                this.showPrayer = true;
                if (isSmallBrowser) {
                    this.showChat = false;
                    this.showDetails = false;
                    this.showGive = false;
                }
            } else if (tab === 'details' && !this.showDetails) {
                this.showDetails = true;
                if (isSmallBrowser) {
                    this.showChat = false;
                    this.showPrayer = false;
                    this.showGive = false;
                }
            } else if (tab === 'give' && !this.showGive) {
                this.showGive = true;
                if (
                    this.settings &&
                    this.settings.give &&
                    this.settings.give.give_form_experience === 'embed'
                ) {
                    this.injectGiveForm("sidebar-embed-give-form-container", this.giveFormEmbedUrl, this.settings.give.give_form_experience);

                }
                if (isSmallBrowser) {
                    this.showChat = false;
                    this.showPrayer = false;
                    this.showDetails = false;
                }
            }

            this.$emit(
                'sidebarToggled',
                this.showChat || this.showPrayer || this.showDetails || this.showGive
            );
        },
        /**
         * Open default tab
         */
        openDefaultTab(settings) {

            if (!settings && !settings.defaulttab) {
                return; // Exit the function early if settings or defaulttab is not defined
            }

            switch (settings.defaulttab) {
                case 'chat':
                    if (settings.chatembed) {
                        this.setOpenTab('chat');
                    }
                    break;

                case 'prayer':
                    if (settings.prayer && settings.prayer.enabled && this.createPrayerUrl) {
                        this.setOpenTab('prayer');
                    }
                    break;

                case 'details':
                    if (this.$slots.details || this.detailsEnabled) {
                        this.setOpenTab('details');
                    }
                    break;

                case 'give':
                    // Special handling for Give - If form experience is 'embed' and open by default set to give row, open the give form in sidebar embed
                    if (settings.give && settings.give.give_form_experience === 'embed') {
                        this.setOpenTab('give');
                    }
                    break;

                default:
                    // Default to 'chat' if no matching tab is found
                    if (settings.chatembed) {
                        this.setOpenTab('chat');
                    }
                    break;
            }
        },

        /**
         * Close give form popup model
         */
        hideGivePopupModal() {
            this.showPopupModel = false;
            this.showGive = false;
        },

        /**
         * Handle trial upgrade click
         */
        handleTrialUpgradeClick() {
            console.log('Trial upgrade clicked - redirect to upgrade page');
            this.$emit('trial-upgrade-clicked');
            // Can add redirect logic here if needed
        },

        /**
         * Inject the Give Form Script dynamically into the DOM
         * Handles both embed and popup experiences
         */
        injectGiveForm(targetElementId, scriptSource, formExperience) {
            if (!scriptSource) {
                console.error("Give Form script source URL is missing.");
                return;
            }

            if (formExperience === 'popup') {
                // If the form experience is popup, show the popup model
                this.showPopupModel = true;
            } else {
                // If the form experience is embed, hide the popup model
                this.showPopupModel = false;
            }

            this.isLoadingGracePeriod = true;

            setTimeout(() => {

                const container = document.getElementById(targetElementId);

                if (!container) {
                    console.error(`Target element with ID "${targetElementId}" not found.`);
                    return;
                }

                // Check if the script is already loaded
                if (!document.querySelector(`#${targetElementId} script`)) {
                    const script = document.createElement("script");
                    script.id = "giveFormScript";
                    script.src = scriptSource;
                    script.async = true;

                    // Mark the script as loaded once it finishes loading
                    script.onload = () => {
                        this.giveFormScriptLoaded = true;
                        this.markIsLoadingGracePeriodFalse();
                    };

                    script.onerror = () => {
                        console.error("Failed to load the Give Form script.");
                        this.markIsLoadingGracePeriodFalse();
                    };

                    // Append the script to the container
                    container.appendChild(script);
                } else {
                    this.giveFormScriptLoaded = true; // Mark as loaded if already present
                    this.markIsLoadingGracePeriodFalse();
                }
            }, 1000); // Grace period of 1 second before injecting the script

        },

        markIsLoadingGracePeriodFalse() {
            setTimeout(() => {
                this.isLoadingGracePeriod = false; // End the grace period after script loads
            }, 1000); // Adjust the timeout as needed
        },

        /**
         * Toggle tab based open close arrow
         */
        toggleTab(tab) {
            const isSmallBrowser = window.innerWidth < this.lgBreakpoint;

            switch (tab) {
                case 'chat':
                    this.showChat = !this.showChat;
                    if (this.showChat && isSmallBrowser) {
                        this.showPrayer = false;
                        this.showDetails = false;
                        this.showGive = false;
                    }
                    break;

                case 'prayer':
                    this.showPrayer = !this.showPrayer;
                    if (this.showPrayer && isSmallBrowser) {
                        this.showChat = false;
                        this.showDetails = false;
                        this.showGive = false;
                    }
                    break;

                case 'details':
                    this.showDetails = !this.showDetails;
                    if (this.showDetails && isSmallBrowser) {
                        this.showChat = false;
                        this.showPrayer = false;
                        this.showGive = false;
                    }
                    break;

                case 'give':
                    this.showGive = !this.showGive;

                    // Trial mode - don't inject any form, just toggle visibility
                    if (this.isAmplifyStreamingTrial) {
                        if (this.showGive && isSmallBrowser) {
                            this.showChat = false;
                            this.showPrayer = false;
                            this.showDetails = false;
                        }
                        break;
                    }

                    // Normal mode - handle form injection
                    if (
                        this.showGive &&
                        this.settings &&
                        this.settings.give &&
                        this.settings.give.give_form_experience === 'popup'
                    ) {
                        // Ensure the script is injected after the modal is rendered
                        this.$nextTick(() => {
                            this.injectGiveForm("sidebar-popup-give-form-container", this.giveFormEmbedUrl, this.settings.give.give_form_experience);
                        });
                    } else if (
                        this.showGive &&
                        this.settings &&
                        this.settings.give &&
                        this.settings.give.give_form_experience === 'embed'
                    ) {
                        this.injectGiveForm("sidebar-embed-give-form-container", this.giveFormEmbedUrl, this.settings.give.give_form_experience);
                    }

                    if (this.showGive && isSmallBrowser) {
                        this.showChat = false;
                        this.showPrayer = false;
                        this.showDetails = false;
                    }
                    break;
            }

            this.$emit(
                'sidebarToggled',
                this.showChat || this.showPrayer || this.showDetails || this.showGive
            );
        }
    }
}
</script>

<style lang="scss">
// Override default sermon cloud styles
body {
    font-size: 16px;
}

html,
body,
#app {
    height: auto;
}

// End sermon cloud overrides
</style>

<style lang="scss" scoped>
@import './resources/sass/biblecloud/variables';

$vendor-tagline-height: 40px;

* {
    font-family: $font-family-openSans;
}

// Customizations to bootstrap
.navbar {
    background: var(--user-header-color);
    min-height: $navbar-height;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: $zindex-dropdown + 1;
}

.navbar-logo-wrapper-img {
    max-height: 60px;
    height: auto;
    padding-right: 20px;
}

.navbar-social {
    display: flex;
    justify-content: center;
}

.navbar-dark .navbar-nav .nav-link {
    color: var(--user-link-color);
    padding-left: 0.625rem;
    padding-right: 0.625rem;

    // Social media icons
    ::v-deep svg {
        fill: var(--user-link-color);
        height: 26px;
    }

    &:hover,
    &:hover ::v-deep svg {
        color: var(--user-alternate-color);
        fill: var(--user-alternate-color);
    }
}

// End bootstrap customizations

// "Sidebar" content is displayed on the right side for larger browsers and under the stream for smaller browsers
.liveStreamAndBibleCloudLayout-sidebar {
    background: $offWhite;
    z-index: $zindex-dropdown - 2;
}

// Toggles should take up the full height on larger browsers and the full width on smaller browsers
.liveStreamAndBibleCloudLayout-sidebar-toggles {
    display: flex;
}

// Toggle headers are the clickable areas that cause expand / collapse
.liveStreamAndBibleCloudLayout-sidebar-toggle-header {
    background: $white;
    border-bottom: 1px solid $lightGray;
    color: $darkGray;
    cursor: pointer;
    font-weight: 500;
    padding: 8px 12px;
}

// Toggle header text contains an icon and text (no chevrons)
.liveStreamAndBibleCloudLayout-sidebar-toggle-header-text {
    align-items: center;
    display: flex;

    ::v-deep svg {
        display: flex;
        fill: $darkGray;
        height: 16px;
        margin-right: 20px;
    }
}

// Toggle body is where the content is displayed
.liveStreamAndBibleCloudLayout-sidebar-toggle-body {
    background: $offWhite;
    padding: 12px;

    &.no-padding {
        padding: 0;
    }
}

.liveStreamAndBibleCloudLayout-sidebar-toggle-body:empty {
    padding: 0;
}

// Vendor tagline displays at the bottom of the page with copyright, icon, and a link to the reseller
.liveStreamAndBibleCloudLayout-vendor-tagline {
    align-items: center;
    background: var(--user-header-color);
    bottom: 0;
    color: var(--user-link-color);
    display: flex;
    height: $vendor-tagline-height;
    justify-content: space-between;
    padding: 10px 12px;
    position: fixed;
    right: 0;
    width: 100%;
    z-index: $zindex-dropdown - 1;
}

// The reseller icon in the tagline
.liveStreamAndBibleCloudLayout-vendor-tagline-icon ::v-deep svg {
    height: 24px;
    fill: var(--user-link-color);
}

// For screens sized "lg" and wider
@include media-breakpoint-up(lg) {
    // Ensure the nav links display on the right side
    .navbar-collapse {
        justify-content: flex-end;
    }

    // Make space at the top and bottom of the page for position: fixed navbar and tagline
    .liveStreamAndBibleCloudLayout-container,
    .liveStreamAndBibleCloudLayout-sidebar {
        padding-bottom: $vendor-tagline-height;
        padding-top: $navbar-height;
    }

    // Ensure the sidebar takes up the full height of the browser window and does not scroll with content
    .liveStreamAndBibleCloudLayout-sidebar {
        border-left: 1px solid $lightGray;
        display: flex;
        height: 100%;
        position: fixed;
        right: 0;
    }

    // Force the sidebar expand / collapse sections to take up the full browser height
    .liveStreamAndBibleCloudLayout-sidebar-toggles {
        flex-direction: column;
        width: 100%;
    }

    // Add up / down arrows to the clickable expand / collapse buttons
    .liveStreamAndBibleCloudLayout-sidebar-toggle-header {
        align-items: center;
        display: flex;
        justify-content: space-between;

        .chevron-arrow ::v-deep svg {
            height: 24px;
            fill: var(--user-alternate-color);
        }

        .chevron-arrow-down {
            display: none;
        }

        .chevron-arrow-up {
            display: inline-block;
        }

        &.collapsed {
            ::v-deep {
                .chevron-arrow-down {
                    display: inline-block;
                }

                .chevron-arrow-up {
                    display: none;
                }
            }
        }
    }

    // Ensure the sidebar sections are evenly sized when expanded
    .liveStreamAndBibleCloudLayout-sidebar-toggle-body {
        flex: 1 1 0;
        overflow: auto;
    }
}

// For screens sized "md" and narrower
@include media-breakpoint-down(md) {
    // Bootstrap customizations
    .navbar-dark .navbar-nav {
        text-align: center;

        // Add lines between each nav link
        .nav-link:not(.nav-link-social) {
            border-bottom: 1px solid $darkGray;
        }
    }

    .navbar-expanded {
        box-shadow: 0 4px 4px $darkGray-80;
    }

    .navbar-expanded-overlay {
        background: $darkGray-80;
        position: fixed;
        top: 0;
        left: 0;
        z-index: $zindex-dropdown;
        height: 100%;
        width: 100%;
    }

    // Style the hamburger menu in the navigation
    .navbar-toggler {
        border-color: var(--user-link-color);
        color: var(--user-link-color);
    }
    // End bootstrap customizations

    // Add space above & below to make room for the navbar, tagline,
    // and sidebar toggles (which are now on the bottom of the page)
    // which are all position: fixed
    .liveStreamAndBibleCloudLayout-container {
        padding-bottom: $vendor-tagline-height + 45px;
        padding-top: $navbar-height;
    }

    // Adjust space below when there is no sidebar
    .liveStreamAndBibleCloudLayout-no-sidebar .liveStreamAndBibleCloudLayout-container {
        padding-bottom: $vendor-tagline-height;
    }

    // Move the sidebar to the bottom of the of the page
    .liveStreamAndBibleCloudLayout-sidebar {
        bottom: $vendor-tagline-height;
        position: static;
    }

    // Ensure the sidebar toggles do not scroll with the page
    .liveStreamAndBibleCloudLayout-sidebar-toggles {
        bottom: $vendor-tagline-height;
        position: fixed;
        width: 100%;
    }

    // Make the expand / collapse buttons span the entire width of the page and be a solid color when selected
    .liveStreamAndBibleCloudLayout-sidebar-toggle-header {
        background: $darkGray;
        color: $white;
        flex: 1;
        font-size: 12px;
        padding-bottom: 0;
        text-align: center;

        &.collapsed {
            background: $white;
            color: $darkGray;

            // Update the sidebar toggle icon colors
            .liveStreamAndBibleCloudLayout-sidebar-toggle-header-text ::v-deep svg {
                fill: $darkGray;
            }
        }

        .liveStreamAndBibleCloudLayout-sidebar-toggle-header-text {
            display: block;

            ::v-deep {
                svg {
                    display: block;
                    fill: $white;
                    margin: auto;
                }
            }
        }

        // Smaller browser will not show the sidebar toggle arrows
        ::v-deep .chevron-arrow {
            display: none;
        }

        .liveStreamAndBibleCloudLayout-sidebar-toggle-body + & {
            border-left: 1px solid $lightGray;
        }
    }

    // Make the sidebar sections full width and display above the expand / collapse buttons
    .liveStreamAndBibleCloudLayout-sidebar-toggle-body {
        border-top: 1px solid $lightGray;
        bottom: 44px;
        // height: 35vh; // Old
        height: auto;
        min-height: 180px;
        overflow: auto;
        position: absolute;
        width: 100%;
        left: 0;
    }

    // Make the tagline smaller to fit smaller browsers better
    .liveStreamAndBibleCloudLayout-vendor-tagline {
        font-size: 12px;
    }

    .flex-container {
        display: flex;
        flex: 1;
        border: 0.5px solid $lightGray;
        border-left: 0.25px solid $lightGray;
        border-right: 0.25px solid $lightGray;
    }
    .flex-container:first-of-type {
        border-left: 0.5px solid $lightGray;
    }
    .flex-container:last-of-type {
        border-right: 0.5px solid $lightGray;
    }
}

::v-deep {

    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);

        .modal-content {
            position: relative;
            display: flex;
            flex-direction: column;
            width: auto !important;
            pointer-events: auto;
            border: none !important;
            background: none !important;
            padding-top: 25px !important;
        }
    }

    .close {
        font-size: 1.5rem;
        font-weight: bold;
        line-height: 1;
        color: #000;
        opacity: 0.5;
        cursor: pointer;
        position: relative;
        top: 68px;
        right: 15px;
        display: flex;
        width: 22px;
    }

    .close:hover {
        color: #000;
        text-decoration: none;
        cursor: pointer;
        opacity: 0.75;
    }

    .give-form-not-found {
        height: 80vh;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    #custom-give-form-modal iframe {
        height: 80vh;
        width: 100%;
    }

    // IFrame Design.
    body.giving-form-ui #mb-formbuilder-container .mb-formbuilder {
        width: 100%;
        height: 100%;
        overflow: hidden;
    }

    body.giving-form-ui #mb-formbuilder-container .mb-formbuilder .mb-formbuilder-header {
        display: none;
    }

    body.giving-form-ui #mb-formbuilder-container .mb-formbuilder .mb-formbuilder-footer {
        display: none;
    }
}

.liveStreamAndBibleCloudLayout-sidebar-toggle-body.give-form {
    padding: 0 !important;
    overflow-y: hidden;
    @media (max-width: 768px) {
        z-index: 10;
        height: calc(100vh - 85vw);
    }
}

.give-form iframe {
    height: 100%;
    width: 100%;
}

.giving-form-sidebar {
    width: 100% !important;
    height: calc(100vh - 210px) !important;
    border: none;
    overflow-y: auto !important;
}

//Scrollbar design
.liveStreamAndBibleCloudLayout-sidebar-toggle-body::-webkit-scrollbar {
    width: 8px; /* Scrollbar width */
}

.liveStreamAndBibleCloudLayout-sidebar-toggle-body::-webkit-scrollbar-thumb {
    border-radius: 4px; /* Rounded scrollbar */
    background: #D8D8D8; /* Scrollbar color */
    box-shadow: 0px 2px 16px 0px rgba(78, 92, 105, 0.04); /* Soft shadow */
}

.liveStreamAndBibleCloudLayout-sidebar-toggle-body::-webkit-scrollbar-track {
    background: transparent; /* Optional track background */
}

#sidebar-embed-give-form-container {
    overflow: auto;
    height: calc(75vh - 30px) !important;
}

#sidebar-popup-give-form-container {
    overflow: auto;
    height: calc(100vh - 30px) !important;
    width: 616px !important;
    scrollbar-width: none;
}

/* Media query for mobile view */
@media (max-width: 767.98px) {
    #sidebar-popup-give-form-container {
        width: 100% !important;
        min-width: 0 !important;
        max-width: 100vw !important;
        overflow: auto;
        height: calc(100vh - 30px) !important;
        scrollbar-width: none;
    }
}

iframe body.giving-form-ui #mb-formbuilder-container {
    margin: 0 !important;
}

.custom-loader-container {
    height: 80vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #666;
}

.custom-loader {
    border: 4px solid #f3f3f3; /* Light grey */
    border-top: 4px solid #3498db; /* Blue */
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

</style>
